import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { APEX_LOGO } from "@/lib/brand";
import { api } from "@/lib/api";

function portalPathForRole(userRole) {
  if (userRole === "carrier") return "/portal/carrier";
  if (["admin", "dispatcher", "biller"].includes(userRole)) return "/portal/tms";
  return "/portal/customer";
}

function displayRoleFor(roleParam, nextTarget) {
  if (nextTarget === "tms") return "TMS Portal";
  if (roleParam === "admin") return "Admin";
  if (roleParam === "carrier") return "Carrier";
  if (roleParam === "customer") return "Customer";
  return "Any";
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const roleParam = sp.get("role");
  const nextTarget = sp.get("next"); // e.g. "tms"
  const displayRole = displayRoleFor(roleParam, nextTarget);
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const validRoles = ["customer", "carrier", "admin"];
  const roleForBackend = validRoles.includes(roleParam) ? roleParam : null;

  const handoffToTmsThenRedirect = async () => {
    // Route into the in-app TMS portal
    nav("/portal/tms");
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    const r = await login(f.email, f.password, roleForBackend);
    setLoading(false);
    if (!r.ok) { setErr(r.error); return; }
    toast.success(`Welcome back, ${r.user.name}`);
    if (nextTarget === "tms") {
      const opened = await handoffToTmsThenRedirect();
      if (opened) return;
    }
    nav(portalPathForRole(r.user.role));
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const roleForCallback = roleParam || "customer";
    const nextParam = nextTarget ? `&next=${encodeURIComponent(nextTarget)}` : "";
    const redirectUrl = window.location.origin + `/auth/callback?role=${roleForCallback}${nextParam}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const canSwitch = roleParam === "customer" || roleParam === "carrier";
  const otherRole = roleParam === "carrier" ? "customer" : "carrier";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="bg-[#0B3D6E] text-white p-10 lg:p-16 hidden lg:flex flex-col justify-between">
        <Link to="/" className="inline-flex items-center bg-white rounded-md p-2">
          <img src={APEX_LOGO} alt="Apex Supply Chain Solutions" className="h-14 w-auto object-contain" />
        </Link>
        <div>
          <h2 className="font-display font-black text-4xl leading-tight">
            {nextTarget === "tms" ? "Sign in to access the TMS." : (roleParam === "carrier" ? "Find loads. Get paid faster." : "Move freight. Grow trust.")}
          </h2>
          <p className="mt-4 text-slate-300 max-w-md">Real-time visibility. Digital documents. A team that answers the phone. That's the Apex portal.</p>
        </div>
        <div className="text-xs text-slate-500">SOC 2 aligned · C-TPAT ready</div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-16 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="font-label text-xs text-[#2E7BC7]">{displayRole}</div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl text-[#0B3D6E]">Sign in</h1>
          <div className="mt-2 text-sm text-slate-600">
            {nextTarget === "tms" ? (
              <>Sign in with your Apex account (customer, carrier, or admin) to continue to Logistics-Command TMS.</>
            ) : canSwitch ? (
              <>Or <button className="text-[#2E7BC7] font-semibold underline" onClick={() => nav(`/login?role=${otherRole}`)} data-testid="switch-role">switch to {otherRole}</button> portal</>
            ) : null}
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Email</label>
              <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#2E7BC7] focus:border-transparent outline-none"
                data-testid="login-email" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Password</label>
              <input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
                className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#2E7BC7] focus:border-transparent outline-none"
                data-testid="login-password" />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
            <button disabled={loading} className="btn-primary-apex w-full" data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs font-label text-slate-400">
            <div className="flex-1 h-px bg-gray-200" /> OR <div className="flex-1 h-px bg-gray-200" />
          </div>

          <button onClick={googleLogin} className="w-full border-2 border-gray-300 rounded-md px-4 py-2.5 font-semibold hover:bg-white transition-colors flex items-center justify-center gap-2" data-testid="google-login-btn">
            <svg className="h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.1 29.2 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.4 2.8l5.7-5.7C33.6 6.9 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.6 19 13 24 13c2.8 0 5.4 1.1 7.4 2.8l5.7-5.7C33.6 6.9 29 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 43c5 0 9.5-1.9 12.9-5l-6-4.9c-2 1.4-4.4 2.3-6.9 2.3-5.2 0-9.6-3.4-11.2-8L6.3 32C9.6 37.6 16.3 43 24 43z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.8-3.7 5.1l6 4.9c-.4.4 6.4-4.7 6.4-14 0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <p className="mt-8 text-sm text-slate-600 text-center">
            No account? <Link to={`/register?role=${roleParam === "carrier" ? "carrier" : "customer"}`} className="text-[#2E7BC7] font-semibold" data-testid="signup-link">Register</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
